import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isSplitDefined } from '@/api/getSplitDetails';
import ArrowLeft from '@/assets/images/arrow-left.svg?react';
import { useCheckoutContext } from '@/context/CheckoutContext/CheckoutContext';
import { useBill } from '@/hooks/useBill';
import { useBrowserUserId } from '@/hooks/useBrowserUserId';
import { useSplitDetails } from '@/hooks/useSplitDetails';
import { sendBusinessEvent } from '@/screens/Checkout/monitoring';
import { trackEvent } from '@/utils/gtmButtonClicked';
import { calculateFinalTipAmount } from '@/utils/tipCalculations';
import { ConfirmButton } from '../ConfirmButton/ConfirmButton';
import { Price } from '../Price/Price';
import styles from './SplitByItemsMode.module.scss';
import { SplitByItemsRow } from './SplitByItemsRow';
import {
  applyRowKeySelectionToRows,
  applySelectionToRows,
  buildSplitBillItemRows,
  calculateCountBasedAmount,
  countLineItemIds,
  explodeBillToSplitItems,
  type SelectableSplitBillItemRow,
  selectedLineItemIdsFromRows,
  toggleRowKeySelection,
} from './splitByItems.util';

interface Props {
  orderNumber: string;
  onBack?: () => void;
}

export const SplitByItemsMode = ({ orderNumber, onBack }: Props) => {
  const { t } = useTranslation();
  const browserUserId = useBrowserUserId();
  const mountedRef = useRef(false);
  const {
    state: {
      selectedLineItemIds: confirmedLineItemIds,
      selectedTipPercentage,
      customTipAmount,
      billTotal,
    },
    actions,
  } = useCheckoutContext();
  const { data: bill, isPending: isBillPending, isError: isBillError } = useBill();
  const { data: splitDetails, isLoading: isSplitDetailsLoading } = useSplitDetails({
    orderNumber,
    enablePolling: true,
    pollingInterval: 15,
  });

  // null = untouched: ticks project from the confirmed multiset (count-based,
  // fills from the top). After the first tap, ticks live on the exact rows the
  // payer clicked — identity stays count-based (ADR 0003), only display follows
  // the finger.
  const [tickedRowKeys, setTickedRowKeys] = useState<ReadonlySet<string> | null>(null);

  const rows = useMemo(() => {
    const lockedLineItemIds = isSplitDefined(splitDetails)
      ? splitDetails.lockedLineItemIds || []
      : [];
    const builtRows = buildSplitBillItemRows(
      explodeBillToSplitItems(bill?.positions || [], bill?.grossDiscount || 0),
      lockedLineItemIds
    );

    return tickedRowKeys === null
      ? applySelectionToRows(builtRows, countLineItemIds(confirmedLineItemIds))
      : applyRowKeySelectionToRows(builtRows, tickedRowKeys);
  }, [bill?.positions, bill?.grossDiscount, splitDetails, tickedRowKeys, confirmedLineItemIds]);

  const selectedLineItemIds = selectedLineItemIdsFromRows(rows);
  // Count-based, NOT the sum of the tapped rows: the committed/charged amount
  // must be derivable from counts alone on every device (see the util's doc)
  const selectedAmount = calculateCountBasedAmount(rows);
  const leftToPay = isSplitDefined(splitDetails)
    ? splitDetails.remainingAmount
    : (bill?.baseTotal ?? billTotal);
  // Remove is only offered while the split is still local (this payer's own
  // confirmed selection, not yet registered on the backend) — once it exists
  // in BE, removing it here would desync from what other payers already see.
  const isSplitRegistered = isSplitDefined(splitDetails);
  const canConfirm = selectedLineItemIds.length > 0;

  useEffect(() => {
    if (mountedRef.current) {
      return;
    }

    mountedRef.current = true;
    trackEvent('split_view', {
      browser_user_id: browserUserId,
      split_type: 'split_by_items',
    });
  }, [browserUserId]);

  const toggleRow = (row: SelectableSplitBillItemRow) => {
    if (row.isLocked) {
      return;
    }

    setTickedRowKeys(toggleRowKeySelection(rows, row));
  };

  const removeSplit = () => {
    trackEvent('remove_split', {
      split_type: 'split_by_items',
      browser_user_id: browserUserId,
      left_to_pay: leftToPay.toString(),
      your_share: selectedAmount.toString(),
    });
    actions.removeSplit();
  };

  const handleConfirm = () => {
    if (!canConfirm) {
      return;
    }

    trackEvent('split_confirmed', {
      split_type: 'split_by_items',
      browser_user_id: browserUserId,
      left_to_pay: leftToPay.toString(),
      your_share: selectedAmount.toString(),
    });

    const tipToPay = calculateFinalTipAmount(
      customTipAmount,
      selectedAmount,
      selectedTipPercentage
    );
    actions.confirmItemSplit(selectedLineItemIds, selectedAmount, tipToPay);

    sendBusinessEvent('kitopay.split_bill.confirmed', {
      split_type: 'split_by_items',
      yourShare: selectedAmount,
      leftToPay,
    });
  };

  if (isBillPending || isSplitDetailsLoading) {
    return <p className={styles.emptyState}>{t('checkout.withAmountDebounce.loading')}</p>;
  }

  if (isBillError || rows.length === 0) {
    return <p className={styles.emptyState}>{t('checkout.splitByItems.noItems')}</p>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        {onBack && (
          <button
            type="button"
            className={styles.backButton}
            onClick={onBack}
            aria-label={t('checkout.splitByItems.back')}
          >
            <ArrowLeft />
          </button>
        )}
        <h3 className={styles.title}>{t('checkout.splitByItems.title')}</h3>
      </div>

      <div className={styles.items}>
        {rows.map(row => (
          <SplitByItemsRow key={row.key} row={row} onToggle={toggleRow} />
        ))}
      </div>

      <div className={styles.billDetails}>
        <div className={styles.billRow}>
          <span>{t('checkout.splitByItems.leftToPay')}</span>
          <span data-testid="split-by-items-left-to-pay">
            <Price amount={leftToPay} />
          </span>
        </div>
        <div className={`${styles.billRow} ${styles.yourShare}`}>
          <span>{t('checkout.splitByItems.yourShare')}</span>
          <span data-testid="split-by-items-your-share">
            <Price amount={selectedAmount} />
          </span>
        </div>

        <div className={styles.buttonContainer}>
          {!isSplitRegistered && (
            <ConfirmButton
              variant="remove"
              onClick={removeSplit}
              text={t('checkout.splitByItems.removeSplit')}
            />
          )}
          <ConfirmButton
            variant="confirm"
            onClick={handleConfirm}
            disabled={!canConfirm}
            text={t('checkout.splitByItems.confirm')}
          />
        </div>
      </div>
    </div>
  );
};
